<script setup lang="ts">
import { GTMContentGroup } from '~/lib/models/gtm'
import { BlockRedirection } from '~/lib/services/content/content.blocks.dtos'

const props = defineProps<{ block: BlockRedirection }>()

const route = useRoute()
const queryParams = route.query
const { t } = useLocale()
const { trackPageView } = useGTM()

const instantRedirect = props.block.instantRedirect
const hasError = ref(false)

const finalUrl = computed(() => {
  const destination = props.block.destination
  if (!props.block.forwardedQueryParams) return destination
  try {
    const forwardedQueryParams = props.block.forwardedQueryParams.split(/[,.]/)
    const targetUrl = new URL(destination)
    for (const forwardedParam of forwardedQueryParams) {
      if (queryParams[forwardedParam])
        targetUrl.searchParams.append(forwardedParam, queryParams[forwardedParam] as string)
    }
    return targetUrl.toString()
  } catch (error) {
    return destination
  }
})

const waitXSeconds = (seconds = 1) => {
  return new Promise<void>(resolve => {
    setTimeout(resolve, seconds * 1000)
  })
}

if (instantRedirect) {
  await navigateTo({ path: finalUrl.value }, { external: true, redirectCode: 301 })
}

onMounted(async () => {
  if (!instantRedirect) {
    const redirectionPageView = {
      cg: GTMContentGroup.primary.REDIRECTION,
      pageTitle: `${GTMContentGroup.primary.HOME}${route.fullPath}`,
      linkUrl: finalUrl.value,
    }

    trackPageView(redirectionPageView)

    const countdownSeconds = 5
    for (let i = 1; i <= countdownSeconds; i++) {
      await waitXSeconds()
    }

    try {
      const target = '_self'
      window.open(finalUrl.value, target)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error while redirecting ', error)
      hasError.value = true
    }
  }
})
</script>

<template>
  <section
    data-test="redirection-container"
    class="fixed left-0 top-0 z-[51] flex h-[100svh] w-full flex-col items-center justify-center bg-white"
  >
    <div class="w-[80%] text-center md:w-[60%]">
      <!-- Title error -->
      <p v-if="hasError" class="mb-4 text-lg font-bold md:text-2xl">
        {{ t('redirection.error.title') }}
      </p>

      <!-- Image -->
      <img
        class="mx-auto h-auto w-full md:w-[30rem]"
        src="/images/fries-falling.gif"
        alt="redirecting"
      />
      <!-- Text -->
      <p v-if="!hasError" class="text-lg font-bold md:text-2xl">{{ block.countdownMessage }}</p>

      <!-- Link fallback error -->
      <NuxtLink
        v-else
        :to="finalUrl"
        external
        class="cursor-pointer text-sm text-mcd-secondaryBlue"
      >
        {{ t('redirection.error.fallbackLink') }}
      </NuxtLink>
    </div>
  </section>
</template>
